import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import { cn } from "@/lib/cn";

/**
 * Workshop: Canvas viewport — mock canvas area with dot grid background.
 * No React Flow dependency — renders children with absolute positioning.
 *
 * Also exports CanvasTransformContext + useCanvasTransform for journey pages
 * to read current zoom/pan state and adjust drag deltas accordingly.
 */

// ─── Transform context ────────────────────────────────────────────────────────

export interface CanvasTransformState {
  zoom: number; // 0.25 – 2.0 (fraction, not percentage)
  panX: number; // canvas translate X in px
  panY: number; // canvas translate Y in px
  isPanMode: boolean;
}

const DEFAULT: CanvasTransformState = {
  zoom: 1,
  panX: 0,
  panY: 0,
  isPanMode: false,
};

export const CanvasTransformContext =
  createContext<CanvasTransformState>(DEFAULT);

export function useCanvasTransform(): CanvasTransformState {
  return useContext(CanvasTransformContext);
}

// ─── CanvasViewport component ─────────────────────────────────────────────────

interface CanvasViewportProps {
  children?: ReactNode;
  showGrid?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function CanvasViewport({
  children,
  showGrid = true,
  className,
  "data-testid": testId = "canvas-viewport",
}: CanvasViewportProps) {
  const { zoom } = useCanvasTransform();
  const dotR = `${1 / zoom}px`;
  const spacing = `${24 / zoom}px`;

  return (
    <div
      className={cn(
        "relative h-full w-full overflow-hidden bg-white dark:bg-black",
        className,
      )}
      data-testid={testId}
    >
      {showGrid && (
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden="true"
          style={{
            backgroundImage: `radial-gradient(circle, var(--color-canvas-dot) ${dotR}, transparent ${dotR})`,
            backgroundSize: `${spacing} ${spacing}`,
          }}
          data-testid="canvas-grid"
        />
      )}
      {children}
    </div>
  );
}

export type { CanvasViewportProps };
